import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Cpu, CheckCircle, XCircle } from 'lucide-react';

interface SystemSignature {
  id: string;
  hash: string;
  signature: string;
  platform: string | null;
  hostname: string | null;
  architecture: string | null;
  cpuId: string | null;
  macAddress: string | null;
  diskSerial: string | null;
  status: string;
  system_info?: {
    platform?: string;
    hostname?: string;
    architecture?: string;
    unique_id?: string;
    mac_addresses?: string[];
    disk_serial?: string;
    cpus?: number;
    memory?: number;
  };
  createdAt?: number;
  created_at?: number;
  verifiedAt?: number;
}

function SystemSignatures() {
  const { t } = useTranslation();
  const [signatures, setSignatures] = useState<SystemSignature[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedSignatureId, setSelectedSignatureId] = useState<string | null>(null);

  useEffect(() => {
    fetchSignatures();
    fetchStatistics();
  }, []);

  const fetchSignatures = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/system-signatures`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSignatures(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch system signatures:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/system-signatures/statistics`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.data || {});
      }
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/system-signatures/${id}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchSignatures();
      }
    } catch (error) {
      console.error('Failed to approve signature:', error);
    }
  };

  const handleRejectSignature = async () => {
    if (!selectedSignatureId) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/system-signatures/${selectedSignatureId}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setRejectDialogOpen(false);
        setSelectedSignatureId(null);
        fetchSignatures();
      }
    } catch (error) {
      console.error('Failed to reject signature:', error);
    }
  };

  const handleRevokeSignature = async () => {
    if (!selectedSignatureId) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/system-signatures/${selectedSignatureId}/revoke`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setRevokeDialogOpen(false);
        setSelectedSignatureId(null);
        fetchSignatures();
      }
    } catch (error) {
      console.error('Failed to revoke signature:', error);
    }
  };

  const openRejectDialog = (id: string) => {
    setSelectedSignatureId(id);
    setRejectDialogOpen(true);
  };

  const openRevokeDialog = (id: string) => {
    setSelectedSignatureId(id);
    setRevokeDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Security"
        icon={Cpu}
        title={t('systemSignatures.title')}
        subtitle={t('systemSignatures.subtitle')}
      />

      {/* Statistics Card */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('systemSignatures.statistics.total')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statistics.total || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-yellow-600">{t('systemSignatures.statistics.pending')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statistics.pending || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-green-600">{t('systemSignatures.statistics.verified')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statistics.verified || statistics.approved || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-red-600">{t('systemSignatures.statistics.revoked')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{statistics.revoked || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('systemSignatures.allSignatures')}</CardTitle>
          <CardDescription>
            {t('systemSignatures.description', { count: signatures.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('systemSignatures.table.signature')}</TableHead>
                <TableHead>{t('systemSignatures.table.platform')}</TableHead>
                <TableHead>{t('systemSignatures.table.cpuId')}</TableHead>
                <TableHead>{t('licenses.status')}</TableHead>
                <TableHead>{t('licenses.createdAt')}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {signatures.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="empty-state text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                signatures.map((sig) => (
                  <TableRow key={sig.id}>
                    <TableCell className="font-mono text-xs max-w-xs truncate" title={sig.hash}>
                      {sig.signature || sig.hash?.substring(0, 20) + '...' || sig.id}
                    </TableCell>
                    <TableCell>
                      {sig.platform ? (
                        <Badge variant="outline">{sig.platform}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-xs truncate" title={sig.cpuId || ''}>
                      {sig.cpuId || '-'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={sig.status} />
                    </TableCell>
                    <TableCell>
                      {formatDate(sig.created_at || sig.createdAt)}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      {sig.status === 'PENDING' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleApprove(sig.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            {t('systemSignatures.approve')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRejectDialog(sig.id)}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            {t('systemSignatures.reject')}
                          </Button>
                        </>
                      )}
                      {(sig.status === 'VERIFIED' || sig.status === 'ACTIVE') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openRevokeDialog(sig.id)}
                        >
                          {t('systemSignatures.revoke')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reject Confirmation Dialog */}
      <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('systemSignatures.dialogs.reject.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('systemSignatures.dialogs.reject.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRejectSignature}>
              {t('systemSignatures.reject')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('systemSignatures.dialogs.revoke.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('systemSignatures.dialogs.revoke.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevokeSignature}>
              {t('systemSignatures.revoke')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SystemSignatures;
